import {
  answerReferenceRegistry,
  parseAnswerReferenceAccessor,
  type AnswerReferenceAccessor,
} from "./answerReferenceRegistry";
import type { InterviewItemTemplateId } from "@/components/interview/types";
import { notNull } from "@/utils/helpers";

const UUID_PATTERN =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

const ACCESSOR_PATTERN = answerReferenceRegistry
  .map((def) => def.accessor)
  .filter(notNull)
  .join("|");

// In plaintext the patterns are: {{answer:UUID}} and {{answer:UUID:accessor}}
export const ANSWER_REFERENCE_PATTERN = new RegExp(
  `\\{\\{answer:(${UUID_PATTERN})(?::(${ACCESSOR_PATTERN}))?\\}\\}`,
  "g"
);

export type AnswerReference = {
  templateId: InterviewItemTemplateId;
  accessor: AnswerReferenceAccessor | undefined;
};

export const formatAnswerReference = ({
  templateId,
  accessor,
}: AnswerReference): string =>
  accessor
    ? `{{answer:${templateId}:${accessor}}}`
    : `{{answer:${templateId}}}`;

export const extractAnswerReferences = (
  text: string
): InterviewItemTemplateId[] => {
  const matches = [...text.matchAll(ANSWER_REFERENCE_PATTERN)];
  return matches.map((m) => m[1] as InterviewItemTemplateId);
};

export const extractAnswerReferencesDetailed = (
  text: string
): AnswerReference[] => {
  const matches = [...text.matchAll(ANSWER_REFERENCE_PATTERN)];
  return matches.map((m) => ({
    templateId: m[1] as InterviewItemTemplateId,
    accessor: parseAnswerReferenceAccessor(m[2]),
  }));
};

export const remapAnswerReferences = (
  text: string,
  templateIdMapping: Map<InterviewItemTemplateId, InterviewItemTemplateId>
): string =>
  text.replace(ANSWER_REFERENCE_PATTERN, (match, oldId: string, accessor) => {
    const newId = templateIdMapping.get(oldId as InterviewItemTemplateId);
    if (!newId) return match;
    return accessor ? `{{answer:${newId}:${accessor}}}` : `{{answer:${newId}}}`;
  });
